import type { Components } from '@/api/generated/api';
import { useCompetenceAssessment } from '@/composables/competenceAssessment/useCompetenceAssessment';
import { usePerformanceAssessmentEntryMutations } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentEntryMutations';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { usePerformanceAssessmentStudentEntryMutations } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentStudentEntryMutations';
import type { TStudentLike } from '@/interfaces';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { areAllEligibleStudentsSelected } from '@/utils/students.utils';
import { arrayDiff, uniquePrimitives } from 'common/utils/array.utils';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { IPerformanceAssessmentEntryStudentView } from './performanceAssessmentEntryDetailDialog.types';

interface IPerformanceAssessmentEntryRelationsComposable {
  addCompetence: (competenceId: number) => void;
  allEligibleStudentsSelected: ComputedRef<boolean>;
  isSaving: Ref<boolean>;
  removeCompetence: (competenceId: number) => Promise<void>;
  reset: () => void;
  selectAllStudents: () => Promise<void>;
  selectedStudents: ComputedRef<TStudentLike[]>;
  updateCompetences: (competenceIds: number[]) => Promise<void>;
  updateStudents: (students: TStudentLike[]) => Promise<void>;
}

export const usePerformanceAssessmentEntryRelations = (params: {
  disabled: Ref<boolean>;
  selectableStudents: Ref<TStudentLike[]>;
  entry: Ref<Components.Schemas.PerformanceAssessmentEntryDto | null>;
  refreshEntry: (entry?: Components.Schemas.PerformanceAssessmentEntryDto) => Promise<void>;
  studentViews: Ref<IPerformanceAssessmentEntryStudentView[]>;
}): IPerformanceAssessmentEntryRelationsComposable => {
  const { currentCompetenceAssessment } = useCompetenceAssessment();
  const { t } = useI18n();
  const { confirm } = useConfirmDialog();
  const {
    addCompetencesToPerformanceAssessmentEntry,
    removeCompetenceFromPerformanceAssessmentEntry,
    setCompetencesOnPerformanceAssessmentEntry,
  } = usePerformanceAssessmentEntryMutations();
  const { syncPerformanceAssessmentStudentEntries } = usePerformanceAssessmentStudentEntryMutations();
  const trialUpsellStore = useTrialUpsellStore();
  const isSaving = ref(false);

  const selectedStudents = computed<TStudentLike[]>(() => params.studentViews.value.map(({ student }) => student));

  const allEligibleStudentsSelected = computed(() =>
    areAllEligibleStudentsSelected(selectedStudents.value, params.selectableStudents.value),
  );

  // every student of the entry is documented, including those that meanwhile lost access
  const countNewCompetenceObservations = (
    entry: Components.Schemas.PerformanceAssessmentEntryDto,
    competenceIds: number[],
  ): number => {
    const entryStudentIds = new Set(entry.studentEntries.map(({ studentId }) => studentId));
    const targetCompetenceIds = new Set(competenceIds);
    const existingObservationCount = entry.competenceObservations.filter(
      ({ competenceId, studentId }) => targetCompetenceIds.has(competenceId) && entryStudentIds.has(studentId),
    ).length;

    return targetCompetenceIds.size * entryStudentIds.size - existingObservationCount;
  };

  const confirmStudentRemoval = (): Promise<boolean> =>
    confirm({
      title: t('performanceAssessmentEntry.detailDialog.confirmRemoveStudents.title'),
      description: t('performanceAssessmentEntry.detailDialog.confirmRemoveStudents.description'),
      confirmButtonLabel: t('general.remove'),
      declineButtonLabel: t('general.cancel'),
      declineOnClose: true,
      size: 'lg',
      theme: 'danger',
    });

  const confirmCompetenceRemoval = (): Promise<boolean> =>
    confirm({
      title: t('performanceAssessmentEntry.detailDialog.confirmRemoveCompetence.title'),
      description: t('performanceAssessmentEntry.detailDialog.confirmRemoveCompetence.description'),
      confirmButtonLabel: t('general.remove'),
      declineButtonLabel: t('general.cancel'),
      declineOnClose: true,
      size: 'lg',
      theme: 'danger',
    });

  const removedStudentsHaveRatings = (targetIds: Set<number>): boolean =>
    params.studentViews.value.some(
      (studentView) =>
        !targetIds.has(studentView.student.id) &&
        (studentView.gradeValue != null ||
          studentView.overallRating !== null ||
          studentView.competenceRatings.length > 0),
    );

  const competenceHasRatings = (competenceId: number): boolean => {
    return (
      params.entry.value?.competenceObservations.some(
        (observation) => observation.competenceId === competenceId && observation.performanceLevelV2Id != null,
      ) ?? false
    );
  };

  const updateStudents = async (students: TStudentLike[]): Promise<void> => {
    const entry = params.entry.value;
    if (!entry || params.disabled.value || isSaving.value) return;

    const targetStudents = students.length ? students : params.selectableStudents.value;
    const targetIds = new Set(targetStudents.map((student) => student.id));
    const currentIds = new Set(entry.studentEntries.map((studentEntry) => studentEntry.studentId));
    const addedStudentIds = [...targetIds].filter((studentId) => !currentIds.has(studentId));
    const removedStudentEntries = entry.studentEntries.filter((studentEntry) => !targetIds.has(studentEntry.studentId));
    if (!addedStudentIds.length && !removedStudentEntries.length) return;

    isSaving.value = true;
    try {
      if (removedStudentEntries.length && removedStudentsHaveRatings(targetIds) && !(await confirmStudentRemoval())) {
        return;
      }
      await syncPerformanceAssessmentStudentEntries({
        schoolClassId: entry.schoolClassId,
        addedStudentIds,
        performanceAssessmentEntryId: entry.id,
        removedStudentEntryIds: removedStudentEntries.map((studentEntry) => studentEntry.id),
      });
      await params.refreshEntry();
    } finally {
      isSaving.value = false;
    }
  };

  const selectAllStudents = (): Promise<void> => updateStudents(params.selectableStudents.value);

  const removePersistedCompetence = async (competenceId: number): Promise<void> => {
    const entry = params.entry.value;
    if (!entry || params.disabled.value || isSaving.value) return;
    isSaving.value = true;
    try {
      if (competenceHasRatings(competenceId) && !(await confirmCompetenceRemoval())) return;
      await removeCompetenceFromPerformanceAssessmentEntry({
        schoolClassId: entry.schoolClassId,
        entryId: entry.id,
        competenceId,
      });
      await params.refreshEntry();
    } finally {
      isSaving.value = false;
    }
  };

  const addPersistedCompetence = async (competenceId: number): Promise<void> => {
    const entry = params.entry.value;
    const competenceAssessment = currentCompetenceAssessment.value;
    if (!entry || !competenceAssessment || params.disabled.value || isSaving.value) return;

    const newObservationCount = countNewCompetenceObservations(entry, [competenceId]);

    isSaving.value = true;
    try {
      await addCompetencesToPerformanceAssessmentEntry({
        schoolClassId: entry.schoolClassId,
        entryId: entry.id,
        dto: {
          competenceIds: [competenceId],
          competenceAssessmentId: competenceAssessment.id,
        },
      });
      trialUpsellStore.recordCompetenceObservationsCreated(newObservationCount);
      trialUpsellStore.requestEligibilityCheck();
      await params.refreshEntry();
    } finally {
      isSaving.value = false;
    }
  };

  const addCompetence = (competenceId: number): void => {
    if (params.disabled.value || isSaving.value) return;
    void addPersistedCompetence(competenceId);
  };

  const removeCompetence = async (competenceId: number): Promise<void> => {
    await removePersistedCompetence(competenceId);
  };

  const updateCompetences = async (competenceIds: number[]): Promise<void> => {
    const entry = params.entry.value;
    const competenceAssessment = currentCompetenceAssessment.value;
    if (!entry || !competenceAssessment || params.disabled.value || isSaving.value) return;

    const currentIds = uniquePrimitives(entry.competenceObservations.map((observation) => observation.competenceId));
    const { toAdd, toRemove } = arrayDiff(currentIds, uniquePrimitives(competenceIds));
    if (!toAdd.length && !toRemove.length) return;

    isSaving.value = true;
    try {
      if (toRemove.some(competenceHasRatings) && !(await confirmCompetenceRemoval())) return;

      const newObservationCount = countNewCompetenceObservations(entry, toAdd);

      const updatedEntry = await setCompetencesOnPerformanceAssessmentEntry({
        schoolClassId: entry.schoolClassId,
        entryId: entry.id,
        dto: {
          competenceIds: uniquePrimitives(competenceIds),
          competenceAssessmentId: competenceAssessment.id,
        },
      });
      if (toAdd.length) {
        trialUpsellStore.recordCompetenceObservationsCreated(newObservationCount);
        trialUpsellStore.requestEligibilityCheck();
      }
      await params.refreshEntry(updatedEntry);
    } finally {
      isSaving.value = false;
    }
  };

  const reset = (): void => {
    isSaving.value = false;
  };

  return {
    addCompetence,
    allEligibleStudentsSelected,
    isSaving,
    removeCompetence,
    reset,
    selectAllStudents,
    selectedStudents,
    updateCompetences,
    updateStudents,
  };
};
